import React, { useState, useEffect, useContext } from 'react';
import { Box, Grid, Card, CardContent, Typography, Avatar, Chip, CircularProgress, Button, List, ListItem, ListItemText, ListItemAvatar, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton } from '@mui/material';
import { People, CalendarToday, Phone, Warning, CheckCircle, TrendingUp, Schedule, Refresh, Close, Download, OpenInNew, Cancel } from '@mui/icons-material';
import axios from 'axios';
import { PieChart, Pie, Cell, Tooltip as ChartTooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = '/api';
const SC = {new:'#2196F3',contacted:'#FF9800',qualified:'#9C27B0',converted:'#4CAF50',lost:'#F44336'};
const OC = {answered:'#4CAF50','no-answer':'#F44336',busy:'#FF9800',interested:'#2196F3'};

const downloadCSV = (data, filename) => {
  if (!data || !data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(','), ...data.map(row => keys.map(k => {
    const val = row[k] === null || row[k] === undefined ? '' : String(row[k]);
    return val.includes(',') || val.includes('"') ? `"${val.replace(/"/g, '\\"')}"` : val;
  }).join(','))].join('\n');
  const blob = new Blob([csv], {type:'text/csv'});
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename + '_' + new Date().toISOString().slice(0,10) + '.csv';
  a.click(); URL.revokeObjectURL(url);
};

const StatCard = ({title, value, icon, color, sub, onClick, onSubClick, isAdmin}) => (
  <Card sx={{height:'100%', border:'1px solid #E4E8EE', boxShadow:'none', cursor:onClick?'pointer':'default',
    '&:hover': onClick ? {boxShadow:'0 4px 20px rgba(0,0,0,0.08)', transform:'translateY(-2px)'} : {},
    transition:'all 0.2s'}}>
    <CardContent onClick={onClick}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
          <Typography variant="h4" fontWeight={700} color={color} sx={{fontFamily:"'IBM Plex Mono', monospace", textDecoration:onClick?'underline':'none',textDecorationStyle:'dotted'}}>{value??0}</Typography>
          {sub&&(onSubClick
            ? <Typography variant="caption" color={color} sx={{textDecoration:'underline',textDecorationStyle:'dotted',cursor:'pointer'}} onClick={e=>{e.stopPropagation();onSubClick();}}>{sub}</Typography>
            : <Typography variant="caption" color="text.secondary">{sub}</Typography>)}
          {onClick&&<Typography variant="caption" sx={{color:color,display:'block',fontSize:10}}>Click to view details</Typography>}
        </Box>
        <Avatar sx={{bgcolor:color+'20',color,width:46,height:46}}>{icon}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const {user} = useContext(AuthContext);
  const navigate = useNavigate();
  const [data,setData] = useState(null);
  const [achData, setAchData] = useState(null);
  const [achPeriod, setAchPeriod] = useState('month');
  const [achMonth, setAchMonth] = useState(new Date().getMonth());
  const [achYear, setAchYear] = useState(new Date().getFullYear());
  const [achQuarter, setAchQuarter] = useState(Math.floor(new Date().getMonth()/3)+1);
  const [achLoading, setAchLoading] = useState(false);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState('');
  const [chartConfig,setChartConfig] = useState({
    showMonthlyTrend:true, showPieChart:true,
    showRecentLeads:true, showUpcomingAppts:true, showRecentCalls:true,
  });
  const [modal,setModal] = useState(null); // {title, rows, columns, filename}
  const [modalLoading,setModalLoading] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const hdr = () => ({headers:{Authorization:'Bearer '+localStorage.getItem('token')}});
  const load = async () => {
    try {
      setLoading(true); setError('');
      const t = localStorage.getItem('token');
      const r = await axios.get(API+'/dashboard',{headers:{Authorization:'Bearer '+t}});
      setData(r.data.data);
    } catch(e) { setError(e.response?.data?.message||'Failed to load dashboard data'); }
    finally { setLoading(false); }
  };
  const loadAchievement = async () => {
    try {
      setAchLoading(true);
      const params = new URLSearchParams({ period: achPeriod, year: achYear, month: achMonth, quarter: achQuarter });
      const r = await axios.get(API+'/dashboard/achievement?'+params, {headers:{Authorization:'Bearer '+localStorage.getItem('token')}});
      if(r.data.success) setAchData(r.data.data);
    } catch(e) { console.error('Achievement load error:', e.message); }
    finally { setAchLoading(false); }
  };

  useEffect(()=>{load();},[]);
  useEffect(()=>{loadAchievement();},[achPeriod,achMonth,achYear,achQuarter]);

  const fetchDetail = async (type, title, filename) => {
    setModalLoading(true);
    setModal({title, rows:[], columns:[], filename, loading:true});
    try {
      const t = localStorage.getItem('token');
      const h = {headers:{Authorization:'Bearer '+t}};
      let rows = [], columns = [];

      if(type==='leads'||type==='converted'||type==='qualified') {
        const statusParam = type==='converted' ? 'status=converted' : type==='qualified' ? 'status=qualified' : '';
        const qs = [statusParam, 'limit=25000'].filter(Boolean).join('&');
        const r = await axios.get(API+'/leads?'+qs,h);
        rows = (r.data.leads||[]).map(l=>({Name:l.name,Phone:l.phone,Email:l.email||'-',Status:l.status,Priority:l.priority,Source:l.source,City:l.address?.split(',').pop()?.trim()||'N/A',Notes:l.notes||'N/A','Created':(l.createdAt?new Date(l.createdAt).toLocaleDateString():'N/A')}));
        columns = ['Name','Phone','Email','Status','Priority','Source','City','Created'];
      } else if(type==='calls') {
        const r = await axios.get(API+'/dashboard/calls-detail',h);
        rows = (r.data.leads||[]).map(l=>({Name:l.name||((l.firstName||'')+' '+(l.lastName||'')).trim()||'N/A',Phone:l.phone||'N/A',City:l.city||'N/A',State:l.state||'N/A',Status:l.status,AssignedTo:l.assignedTo?(l.assignedTo.name||l.assignedTo.email):'Unassigned',Updated:l.updatedAt?new Date(l.updatedAt).toLocaleString():'N/A'}));
        columns = ['Name','Phone','City','State','Status','AssignedTo','Updated'];
      } else if(type==='calls_alltime') {
        const r = await axios.get(API+'/dashboard/calls-alltime-detail',h);
        rows = (r.data.leads||[]).map(l=>({Name:l.name||((l.firstName||'')+' '+(l.lastName||'')).trim()||'N/A',Phone:l.phone||'N/A',City:l.city||'N/A',State:l.state||'N/A',Status:l.status,AssignedTo:l.assignedTo?(l.assignedTo.name||l.assignedTo.email):'Unassigned',Updated:l.updatedAt?new Date(l.updatedAt).toLocaleString():'N/A'}));
        columns = ['Name','Phone','City','State','Status','AssignedTo','Updated'];
      } else if(type==='appointments') {
        const r = await axios.get(API+'/appointments',h);
        rows = (r.data.appointments||[]).map(a=>({Title:a.title,Lead:((a.lead?.name || ((a.lead?.firstName||'') + ' ' + (a.lead?.lastName||'')).trim() || a.lead?.phone || 'Unknown') || ((a.lead?.firstName||'') + ' ' + (a.lead?.lastName||'')).trim() || a.lead?.phone || 'Unknown')||'N/A',Status:a.status,Start:a.startTime?new Date(a.startTime).toLocaleString():'N/A',End:a.endTime?new Date(a.endTime).toLocaleString():'N/A',Location:a.location||'N/A',Notes:a.notes||'N/A'}));
        columns = ['Title','Lead','Status','Start','End','Location'];
      } else if(type==='today_appointments') {
        const r = await axios.get(API+'/dashboard/appointments-today-detail',h);
        rows = (r.data.appointments||[]).map(a=>({Title:a.title,Lead:((a.lead?.name || ((a.lead?.firstName||'') + ' ' + (a.lead?.lastName||'')).trim() || a.lead?.phone || 'Unknown')||'N/A'),'Booked By':a.user?(a.user.name||a.user.email):'N/A','Assigned To':a.assignedSalesperson?(a.assignedSalesperson.name||a.assignedSalesperson.email):'Not Assigned',Status:a.status,Start:a.startTime?new Date(a.startTime).toLocaleString():'N/A',End:a.endTime?new Date(a.endTime).toLocaleString():'N/A',Location:a.location||'N/A'}));
        columns = ['Title','Lead','Booked By','Assigned To','Status','Start','End','Location'];
      } else if(type==='pending_appointments') {
        const r = await axios.get(API+'/dashboard/pending-appointments-detail',h);
        rows = (r.data.appointments||[]).map(a=>({Title:a.title,Lead:((a.lead?.name || ((a.lead?.firstName||'') + ' ' + (a.lead?.lastName||'')).trim() || a.lead?.phone || 'Unknown')||'N/A'),'Booked By':a.user?(a.user.name||a.user.email):'N/A','Assigned To':a.assignedSalesperson?(a.assignedSalesperson.name||a.assignedSalesperson.email):'Not Assigned',Status:a.status,Start:a.startTime?new Date(a.startTime).toLocaleString():'N/A',End:a.endTime?new Date(a.endTime).toLocaleString():'N/A',Location:a.location||'N/A'}));
        columns = ['Title','Lead','Booked By','Assigned To','Status','Start','End','Location'];
      } else if(type==='recalls') {
        const r = await axios.get(API+'/recalls',h);
        rows = (r.data.recalls||[]).map(rc=>({Lead:rc.lead?.name||'N/A',Phone:rc.lead?.phone||'N/A',Status:rc.status,Priority:rc.priority,Scheduled:rc.scheduledAt?new Date(rc.scheduledAt).toLocaleString():'N/A',Reason:rc.reason||'N/A'}));
        columns = ['Lead','Phone','Status','Priority','Scheduled','Reason'];
      } else if(type==='users') {
        const r = await axios.get(API+'/users',h);
        rows = (r.data.users||[]).map(u=>({Name:u.name,Email:u.email,Role:u.role,Phone:u.phone||'N/A',Status:u.isActive?'Active':'Inactive',Joined:u.createdAt?new Date(u.createdAt).toLocaleDateString():'N/A'}));
        columns = ['Name','Email','Role','Phone','Status','Joined'];
      }
      setModal({title, rows, columns, filename, loading:false});
    } catch(e) {
      setModal({title, rows:[], columns:[], filename, loading:false, error:'Failed to load data'});
    } finally { setModalLoading(false); }
  };

  if(loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress sx={{color:'#0B63C9'}}/></Box>;
  if(error) return <Box textAlign="center" mt={6}><Typography color="error" gutterBottom>{error}</Typography><Button variant="contained" startIcon={<Refresh/>} onClick={load} sx={{bgcolor:'#0B63C9'}}>Retry</Button></Box>;

  const {stats,leadsByStatus,monthlyTrend,recentLeads,upcomingAppointments,recentCalls} = data;

  return (
    <Box p={3}>
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h5" fontWeight={700}>Welcome, {user?.name} 👋</Typography>
          <Typography variant="body2" color="text.secondary">Here's your overview for today</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh/>} onClick={load} size="small" sx={{borderColor:'#0B63C9',color:'#0B63C9'}}>Refresh</Button>
      </Box>

      {isAdmin && (
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mb={2}
          sx={{bgcolor:'white',borderRadius:2,p:1.5,border:'1px solid #eee'}}>
          <Typography variant="body2" fontWeight={600} color="text.secondary" mr={1}>
            📊 Customize Dashboard:
          </Typography>
          {['showMonthlyTrend','showPieChart','showRecentLeads','showUpcomingAppts','showRecentCalls'].map(key=>(
            <Chip key={key} label={{
              showMonthlyTrend:'Monthly Trend',
              showPieChart:'Status Chart',
              showRecentLeads:'Recent Leads',
              showUpcomingAppts:'Upcoming Appts',
              showRecentCalls:'Recent Calls',
            }[key]} size="small" onClick={()=>setChartConfig(p=>({...p,[key]:!p[key]}))}
              icon={chartConfig[key]?<CheckCircle sx={{fontSize:'14px !important'}}/>:<Cancel sx={{fontSize:'14px !important'}}/>}
              sx={{cursor:'pointer',
                bgcolor:chartConfig[key]?'#0B63C920':'#f5f5f5',
                color:chartConfig[key]?'#0B63C9':'#999',
                border:chartConfig[key]?'1px solid #0B63C940':'1px solid #eee',
                fontWeight:chartConfig[key]?600:400,
                fontSize:11,
              }}/>
          ))}
        </Box>
      )}
      <Box sx={{display:'grid', gridTemplateColumns:{xs:'repeat(2, 1fr)', sm:'repeat(4, 1fr)', md:'2fr repeat(6, minmax(0,1fr))'}, gap:2, mb:3}}>
        <Box sx={{gridColumn:{xs:'span 2', sm:'span 4', md:'span 1'}}}>
          <Card sx={{height:'100%', border:'1px solid #E4E8EE', boxShadow:'none', cursor:'pointer',
            '&:hover':{boxShadow:'0 4px 20px rgba(0,0,0,0.08)', transform:'translateY(-2px)'},
            transition:'all 0.2s'}} onClick={()=>fetchDetail('leads','All Leads','leads')}>
            <CardContent sx={{p:3}}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="body2" color="text.secondary" fontWeight={600}>Total Leads</Typography>
                <Avatar sx={{bgcolor:'#0B63C920',color:'#0B63C9',width:46,height:46}}><People/></Avatar>
              </Box>
              <Typography sx={{fontFamily:"'IBM Plex Mono', monospace", fontWeight:600, fontSize:{xs:36,sm:48}, color:'#0B63C9', lineHeight:1, mt:1.5}}>
                {stats.totalLeads??0}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mt={2.5}>
                {(leadsByStatus||[]).filter(s=>s.value>0).map(s=>(
                  <Chip key={s.name} label={s.name+': '+s.value} size="small"
                    sx={{bgcolor:s.color+'20', color:s.color, fontWeight:600, fontSize:11}}/>
                ))}
              </Box>
              <Typography variant="caption" sx={{color:'#0B63C9', display:'block', mt:1.5, fontWeight:600}}>
                Click to view details
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box>
          <StatCard title="Today's Appointments" value={stats.todayAppointments} icon={<CalendarToday/>} color="#0B63C9" sub={stats.pendingAppointments+' pending overall'} isAdmin={isAdmin}
            onClick={()=>fetchDetail('today_appointments',"Today's Appointments",'today_appointments')}
            onSubClick={()=>fetchDetail('pending_appointments','All Pending Appointments','pending_appointments')}/>
        </Box>
        <Box>
          <StatCard title="Calls Today" value={stats.todayCalls} icon={<Phone/>} color="#0B63C9" sub={stats.totalCalls+' all-time'} isAdmin={isAdmin}
            onClick={()=>fetchDetail('calls','Today\'s Calls','calls')}
            onSubClick={()=>fetchDetail('calls_alltime','All-Time Calls','calls_alltime')}/>
        </Box>
        <Box>
          <StatCard title="Pending Recalls" value={stats.pendingRecalls} icon={<Warning/>} color={stats.overdueRecalls>0?'#DC2626':'#F0A93A'} sub={stats.overdueRecalls>0?stats.overdueRecalls+' overdue!':'On track'} isAdmin={isAdmin}
            onClick={()=>fetchDetail('recalls','Recall Queue','recalls')}/>
        </Box>
        {isAdmin&&<Box>
          <StatCard title="Active Users" value={stats.totalUsers} icon={<People/>} color="#0B63C9" sub="Team members" isAdmin={isAdmin}
            onClick={()=>fetchDetail('users','Team Members','team')}/>
        </Box>}
        <Box>
          <StatCard title="Converted" value={stats.convertedLeads} icon={<CheckCircle/>} color="#3A7D1C" sub={'of '+stats.totalLeads} isAdmin={isAdmin}
            onClick={()=>fetchDetail('converted','Converted Leads','converted_leads')}/>
        </Box>
        <Box>
          <StatCard title="Qualified" value={stats.qualifiedLeads} icon={<TrendingUp/>} color="#3A7D1C" sub="Ready to convert" isAdmin={isAdmin}
            onClick={()=>fetchDetail('qualified','Qualified Leads','qualified_leads')}/>
        </Box>
        <Box>
          <StatCard title="Upcoming Appts" value={upcomingAppointments?.length||0} icon={<Schedule/>} color="#0B63C9" sub="Scheduled" isAdmin={isAdmin}
            onClick={()=>fetchDetail('appointments','Upcoming Appointments','upcoming_appointments')}/>
        </Box>
      </Box>

      <Box sx={{display:'grid', gridTemplateColumns:{xs:'1fr', md:'2fr 1fr'}, gap:2, mb:3}}>
        <Box sx={{display:chartConfig.showMonthlyTrend?'':'none'}}>
          <Card sx={{border:'1px solid #E4E8EE', boxShadow:'none'}}><CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>Monthly Lead Trend</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFF2F6" vertical={false}/>
                <XAxis dataKey="month" tick={{fontFamily:"'IBM Plex Mono', monospace", fontSize:12, fill:'#5A6472'}} axisLine={{stroke:'#E4E8EE'}} tickLine={false}/>
                <YAxis allowDecimals={false} tick={{fontFamily:"'IBM Plex Mono', monospace", fontSize:11, fill:'#8A93A6'}} axisLine={false} tickLine={false}/>
                <ChartTooltip contentStyle={{fontFamily:"'IBM Plex Sans', sans-serif", borderRadius:8, border:'1px solid #E4E8EE'}}/>
                <Bar dataKey="leads" fill="#0B63C9" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </CardContent></Card>
        </Box>
        <Box sx={{display:chartConfig.showPieChart?'':'none'}}>
          <Card sx={{height:'100%', border:'1px solid #E4E8EE', boxShadow:'none'}}><CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>Leads by Status</Typography>
            {stats.totalLeads===0
              ?<Box display="flex" justifyContent="center" alignItems="center" height={160}><Typography color="text.secondary">No data</Typography></Box>
              :<Box display="flex" alignItems="center" gap={2}>
                <Box sx={{position:'relative', width:150, height:150, flexShrink:0}}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={leadsByStatus.filter(d=>d.value>0)} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={70} paddingAngle={leadsByStatus.filter(d=>d.value>0).length>1?2:0} stroke="none">
                        {leadsByStatus.filter(d=>d.value>0).map((e,i)=><Cell key={i} fill={e.color}/>)}
                      </Pie>
                      <ChartTooltip contentStyle={{fontFamily:"'IBM Plex Sans', sans-serif", borderRadius:8, border:'1px solid #E4E8EE'}}/>
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', pointerEvents:'none'}}>
                    <Typography sx={{fontFamily:"'IBM Plex Mono', monospace", fontWeight:600, fontSize:19, color:'#101828'}}>{stats.totalLeads}</Typography>
                    <Typography sx={{fontSize:10.5, color:'#8A93A6'}}>total</Typography>
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column" gap={1} minWidth={0}>
                  {leadsByStatus.map((s,i)=>(
                    <Box key={i} display="flex" alignItems="center" gap={1}>
                      <Box sx={{width:9, height:9, borderRadius:'50%', bgcolor:s.color, flexShrink:0}}/>
                      <Typography sx={{fontSize:13, color:'#344054', textTransform:'capitalize'}}>{s.name}</Typography>
                      <Typography sx={{fontFamily:"'IBM Plex Mono', monospace", fontSize:13, color:'#101828', ml:'auto', pl:1}}>{s.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>}
          </CardContent></Card>
        </Box>
      </Box>

      <Box sx={{display:'grid', gridTemplateColumns:{xs:'1fr', md:'repeat(3, 1fr)'}, gap:2}}>
        <Box sx={{display:chartConfig.showRecentLeads?'':'none'}}>
          <Card sx={{border:'1px solid #E4E8EE', boxShadow:'none'}}><CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" fontWeight={600}>Recent Leads</Typography>
              <Button size="small" onClick={()=>navigate('/preleads')} sx={{color:'#0B63C9',fontSize:11}}>View All</Button>
            </Box>
            {!recentLeads?.length?<Typography color="text.secondary" variant="body2">No leads yet</Typography>
            :<List dense disablePadding>{recentLeads.map((l,i)=>(
              <React.Fragment key={l._id}>
                <ListItem disableGutters sx={{cursor:'pointer','&:hover':{bgcolor:'#FAFBFC'},borderRadius:1,px:0.5}}>
                  <ListItemAvatar><Avatar sx={{bgcolor:SC[l.status]+'20',color:SC[l.status],width:30,height:30,fontSize:12}}>{l.name?.charAt(0)}</Avatar></ListItemAvatar>
                  <ListItemText primary={<Typography variant="body2" fontWeight={500}>{l.name}</Typography>}
                    secondary={<Chip label={l.status} size="small" sx={{bgcolor:SC[l.status]+'20',color:SC[l.status],fontSize:10,height:17}}/>}/>
                </ListItem>
                {i<recentLeads.length-1&&<Divider/>}
              </React.Fragment>))}</List>}
          </CardContent></Card>
        </Box>
        <Box sx={{display:chartConfig.showUpcomingAppts?'':'none'}}>
          <Card sx={{border:'1px solid #E4E8EE', boxShadow:'none'}}><CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" fontWeight={600}>Upcoming Appointments</Typography>
              <Button size="small" onClick={()=>navigate('/appointments')} sx={{color:'#0B63C9',fontSize:11}}>View All</Button>
            </Box>
            {!upcomingAppointments?.length?<Typography color="text.secondary" variant="body2">No upcoming appointments</Typography>
            :<List dense disablePadding>{upcomingAppointments.map((a,i)=>(
              <React.Fragment key={a._id}>
                <ListItem disableGutters>
                  <ListItemAvatar><Avatar sx={{bgcolor:'#0B63C920',color:'#0B63C9',width:30,height:30}}><CalendarToday sx={{fontSize:15}}/></Avatar></ListItemAvatar>
                  <ListItemText primary={<Typography variant="body2" fontWeight={500}>{((a.lead?.name || ((a.lead?.firstName||'') + ' ' + (a.lead?.lastName||'')).trim() || a.lead?.phone || 'Unknown') || ((a.lead?.firstName||'') + ' ' + (a.lead?.lastName||'')).trim() || a.lead?.phone || 'Unknown')||'Lead'}</Typography>}
                    secondary={<Typography variant="caption">{new Date(a.startTime).toLocaleString()}</Typography>}/>
                </ListItem>
                {i<upcomingAppointments.length-1&&<Divider/>}
              </React.Fragment>))}</List>}
          </CardContent></Card>
        </Box>
        <Box sx={{display:chartConfig.showRecentCalls?'':'none'}}>
          <Card sx={{border:'1px solid #E4E8EE', boxShadow:'none'}}><CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" fontWeight={600}>Recent Calls</Typography>
              <Button size="small" onClick={()=>navigate('/mis-dashboard')} sx={{color:'#0B63C9',fontSize:11}}>View All</Button>
            </Box>
            {!recentCalls?.length?<Typography color="text.secondary" variant="body2">No calls yet</Typography>
            :<List dense disablePadding>{recentCalls.map((c,i)=>(
              <React.Fragment key={c._id}>
                <ListItem disableGutters>
                  <ListItemAvatar><Avatar sx={{bgcolor:(OC[c.outcome]||'#9E9E9E')+'20',color:OC[c.outcome]||'#9E9E9E',width:30,height:30}}><Phone sx={{fontSize:15}}/></Avatar></ListItemAvatar>
                  <ListItemText primary={<Typography variant="body2" fontWeight={500}>{c.lead?.name||'Lead'}</Typography>}
                    secondary={<Chip label={c.outcome} size="small" sx={{bgcolor:(OC[c.outcome]||'#9E9E9E')+'20',color:OC[c.outcome]||'#9E9E9E',fontSize:10,height:17}}/>}/>
                </ListItem>
                {i<recentCalls.length-1&&<Divider/>}
              </React.Fragment>))}</List>}
          </CardContent></Card>
        </Box>
      </Box>

      {/* Detail Modal */}
      <Dialog open={!!modal} onClose={()=>setModal(null)} maxWidth="lg" fullWidth PaperProps={{sx:{borderRadius:3,height:'80vh'}}}>
        {modal&&<>
          <DialogTitle sx={{display:'flex',alignItems:'center',justifyContent:'space-between',pb:1,borderBottom:'1px solid #eee'}}>
            <Typography fontWeight={700} fontSize={18}>{modal.title}
              {modal.rows?.length>0&&<Chip label={modal.rows.length+' records'} size="small" sx={{ml:1,bgcolor:'#0B63C920',color:'#0B63C9'}}/>}
            </Typography>
            <Box display="flex" gap={1}>
              {isAdmin&&modal.rows?.length>0&&(
                <Button variant="contained" size="small" startIcon={<Download/>}
                  onClick={()=>downloadCSV(modal.rows, modal.filename)}
                  sx={{bgcolor:'#3A7D1C','&:hover':{bgcolor:'#2F6716'},borderRadius:2,textTransform:'none',fontWeight:600}}>
                  Download CSV
                </Button>
              )}
              <IconButton size="small" onClick={()=>setModal(null)} sx={{'&:hover':{bgcolor:'#F4433615'}}}><Close/></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{p:0,overflow:'auto'}}>
            {modal.loading?
              <Box display="flex" justifyContent="center" alignItems="center" height={200}><CircularProgress sx={{color:'#0B63C9'}}/></Box>
            :modal.error?
              <Box p={3} textAlign="center"><Typography color="error">{modal.error}</Typography></Box>
            :modal.rows?.length===0?
              <Box p={4} textAlign="center"><Typography color="text.secondary">No data found</Typography></Box>
            :(
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>{modal.columns.map(col=>(
                      <TableCell key={col} sx={{bgcolor:'#F5F7FA',fontWeight:700,fontSize:12,color:'#555'}}>{col}</TableCell>
                    ))}</TableRow>
                  </TableHead>
                  <TableBody>
                    {modal.rows.map((row,i)=>(
                      <TableRow key={i} hover sx={{'&:hover':{bgcolor:'#FAFBFC'}}}>
                        {modal.columns.map(col=>(
                          <TableCell key={col} sx={{fontSize:12,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                            {col==='Status'?<Chip label={row[col]} size="small" sx={{fontSize:10,height:20}}/>:row[col]||'—'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
        </>}
      </Dialog>

      {/* Rejection & Hold Reasons */}
      {(data?.rejectionReasons?.length > 0 || data?.holdReasons?.length > 0) && (
        <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
          {data?.rejectionReasons?.length > 0 && (
            <Box flex={1} minWidth={300}>
              <Typography variant="h6" fontWeight={700} mb={2} fontSize={15}>
                Top Rejection Reasons
                <Typography component="span" fontSize={12} color="text.secondary" ml={1}>
                  ({data?.notInterestedLeads || 0} not interested)
                </Typography>
              </Typography>
              <Box sx={{bgcolor:'white',borderRadius:2,p:2,border:'1px solid #E4E8EE'}}>
                {data.rejectionReasons.map((r,i) => (
                  <Box key={i} mb={1.5}>
                    <Box display="flex" justifyContent="space-between" mb={0.3}>
                      <Typography fontSize={12} color="text.secondary">{r._id}</Typography>
                      <Typography fontSize={12} fontWeight={700} color="#DC2626">{r.count}</Typography>
                    </Box>
                    <Box sx={{height:6,bgcolor:'#f5f5f5',borderRadius:3,overflow:'hidden'}}>
                      <Box sx={{height:'100%',bgcolor:'#DC2626',borderRadius:3,
                        width:(r.count/(data.rejectionReasons[0]?.count||1)*100)+'%',transition:'width 0.5s'}}/>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {data?.holdReasons?.length > 0 && (
            <Box flex={1} minWidth={300}>
              <Typography variant="h6" fontWeight={700} mb={2} fontSize={15}>
                Top On-Hold Reasons
                <Typography component="span" fontSize={12} color="text.secondary" ml={1}>
                  ({data?.onHoldLeads || 0} on hold)
                </Typography>
              </Typography>
              <Box sx={{bgcolor:'white',borderRadius:2,p:2,border:'1px solid #E4E8EE'}}>
                {data.holdReasons.map((r,i) => (
                  <Box key={i} mb={1.5}>
                    <Box display="flex" justifyContent="space-between" mb={0.3}>
                      <Typography fontSize={12} color="text.secondary">{r._id}</Typography>
                      <Typography fontSize={12} fontWeight={700} color="#F0A93A">{r.count}</Typography>
                    </Box>
                    <Box sx={{height:6,bgcolor:'#f5f5f5',borderRadius:3,overflow:'hidden'}}>
                      <Box sx={{height:'100%',bgcolor:'#F0A93A',borderRadius:3,
                        width:(r.count/(data.holdReasons[0]?.count||1)*100)+'%',transition:'width 0.5s'}}/>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Achievement Dashboard */}
      <Box mt={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h6" fontWeight={700} fontSize={16}>Achievement Dashboard</Typography>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {['month','quarter','year'].map(p=>(
              <Button key={p} size="small" variant={achPeriod===p?'contained':'outlined'} onClick={()=>setAchPeriod(p)}
                sx={{minWidth:70,bgcolor:achPeriod===p?'#0B63C9':'transparent',color:achPeriod===p?'white':'#0B63C9',borderColor:'#0B63C9',fontSize:11}}>
                {p.charAt(0).toUpperCase()+p.slice(1)}
              </Button>
            ))}
            {achPeriod==='month'&&(
              <select value={achMonth} onChange={e=>setAchMonth(Number(e.target.value))}
                style={{padding:'4px 8px',borderRadius:4,border:'1px solid #0B63C9',fontSize:12}}>
                {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i)=>(
                  <option key={i} value={i}>{m}</option>
                ))}
              </select>
            )}
            {achPeriod==='quarter'&&(
              <select value={achQuarter} onChange={e=>setAchQuarter(Number(e.target.value))}
                style={{padding:'4px 8px',borderRadius:4,border:'1px solid #0B63C9',fontSize:12}}>
                {[1,2,3,4].map(q=><option key={q} value={q}>Q{q}</option>)}
              </select>
            )}
            <select value={achYear} onChange={e=>setAchYear(Number(e.target.value))}
              style={{padding:'4px 8px',borderRadius:4,border:'1px solid #0B63C9',fontSize:12}}>
              {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
            </select>
            <Button size="small" onClick={loadAchievement} sx={{color:'#0B63C9',borderColor:'#0B63C9'}} variant="outlined">Refresh</Button>
          </Box>
        </Box>

        {achLoading ? <Box display="flex" justifyContent="center" py={3}><CircularProgress size={30} sx={{color:'#0B63C9'}}/></Box> : achData && (
          <Box display="flex" gap={2} flexWrap="wrap">
            {/* State-wise Chart */}
            {false&&achData.stateWise?.length>0&&(
              <Box flex={1} minWidth={300} sx={{bgcolor:'white',borderRadius:2,p:2,border:'1px solid #E4E8EE'}}>
                <Typography fontWeight={700} fontSize={14} mb={1}>State-wise Achievement</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={achData.stateWise.map(d=>({name:d._id||'Unknown',Total:d.total,Converted:d.converted,Qualified:d.qualified}))} margin={{left:-20}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                    <XAxis dataKey="name" tick={{fontSize:10}} angle={-30} textAnchor="end" height={50}/>
                    <YAxis tick={{fontSize:10}}/>
                    <ChartTooltip/>
                    <Legend wrapperStyle={{fontSize:11}}/>
                    <Bar dataKey="Total" fill="#0B63C9" radius={[3,3,0,0]}/>
                    <Bar dataKey="Converted" fill="#3A7D1C" radius={[3,3,0,0]}/>
                    <Bar dataKey="Qualified" fill="#F0A93A" radius={[3,3,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}

            {/* City-wise Chart */}
            {achData.cityWise?.length>0&&(
              <Box flex={1} minWidth={300} sx={{bgcolor:'white',borderRadius:2,p:2,border:'1px solid #E4E8EE'}}>
                <Typography fontWeight={700} fontSize={14} mb={1}>City-wise Achievement</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={achData.cityWise.map(d=>({name:d._id||'Unknown',Total:d.total,Converted:d.converted}))} margin={{left:-20}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                    <XAxis dataKey="name" tick={{fontSize:10}} angle={-30} textAnchor="end" height={50}/>
                    <YAxis tick={{fontSize:10}}/>
                    <ChartTooltip/>
                    <Legend wrapperStyle={{fontSize:11}}/>
                    <Bar dataKey="Total" fill="#0B63C9" radius={[3,3,0,0]}/>
                    <Bar dataKey="Converted" fill="#3A7D1C" radius={[3,3,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}

            {/* Monthly Trend */}
            {achData.monthlyTrend?.length>0&&(
              <Box flex={1} minWidth={300} sx={{bgcolor:'white',borderRadius:2,p:2,border:'1px solid #E4E8EE'}}>
                <Typography fontWeight={700} fontSize={14} mb={1}>Monthly Trend ({achYear})</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={achData.monthlyTrend.map(d=>({name:['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d._id.month-1],Total:d.total,Converted:d.converted}))} margin={{left:-20}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                    <XAxis dataKey="name" tick={{fontSize:10}}/>
                    <YAxis tick={{fontSize:10}}/>
                    <ChartTooltip/>
                    <Legend wrapperStyle={{fontSize:11}}/>
                    <Bar dataKey="Total" fill="#0B63C9" radius={[3,3,0,0]}/>
                    <Bar dataKey="Converted" fill="#3A7D1C" radius={[3,3,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}

            {(!achData.stateWise?.length&&!achData.cityWise?.length)&&(
              <Box width="100%" textAlign="center" py={4} color="text.secondary">
                <Typography>No achievement data for selected period. Upload leads and start calling to see analytics.</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
